import {
  Add as AddIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  UploadFile as UploadFileIcon,
  Download as DownloadIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import CustomPagination from "../../common/custom-pagination";
import { useAuth } from "../../hooks/AuthProvider";
import {
  EDIT_BOTBRIDGE_LEADS,
  LIST_CREATE_BOTBRIDGE_LEADS,
} from "../../config/urls";
import ButtonOne from "../../common/normal-button";
import ButtonTwo from "../../common/dashboard-button";
import AppTable from "../../common/app-table";
import AppChip from "../../common/app-chip";
import { ColorsConstants } from "../../cssUtils/constants";
import typography from "../../cssUtils/typography";
import { formatWithTzLabel } from "../../utils/dateUtils";

// ─── Constants ───────────────────────────────────────────────────────────────

const COLUMNS = [
  { label: "WhatsApp Phone Number", id: "wa_phone_no" },
  {
    label: "WhatsApp Name",
    id: "wa_name",
    render: (row) => row.wa_name || "N/A",
  },
  {
    label: "Phone Number",
    id: "phone_no",
    render: (row) => row.phone_no || "N/A",
  },
  { label: "Name", id: "name", render: (row) => row.name || "N/A" },
  { label: "Company", id: "company", render: (row) => row.company || "N/A" },
  { label: "Email", id: "email", render: (row) => row.email || "N/A" },
  {
    label: "Business Type",
    id: "business_type",
    render: (row) => row.business_type || "N/A",
  },
  {
    label: "Status",
    id: "status",
    render: (row) => (
      <Typography
        color={
          row.status_display === "Interested"
            ? ColorsConstants.successText
            : "inherit"
        }
        fontSize={typography.bodyLarge}
        fontWeight={row.status_display === "Interested" ? "bold" : "normal"}
      >
        {row.status_display || "N/A"}
      </Typography>
    ),
  },
  {
    label: "Interested Flows",
    id: "interested_flows",
    render: (row) =>
      row.interested_flows.length > 0
        ? row.interested_flows.map((flow) => (
            <AppChip key={flow} label={flow} color="primary" sx={{ mr: 1 }} />
          ))
        : "N/A" || "N/A",
  },
  {
    label: "Interested CTAs",
    id: "interested_ctas",
    render: (row) =>
      row.interested_ctas.length > 0
        ? row.interested_ctas.map((cta) => (
            <AppChip key={cta} label={cta} color="secondary" sx={{ mr: 1 }} />
          ))
        : "N/A" || "N/A",
  },
  {
    label: "Last Updated", id: "updated_at",
    render: (row) => (
      <Typography sx={{ ...typography.bodyLarge, fontWeight: 400 }}>
        {row.updated_at_iso ? formatWithTzLabel(row.updated_at_iso) : "N/A"}
      </Typography>
    ),
  },
];

const EMPTY_FORM = {
  wa_phone_no: "",
  wa_name: "",
  phone_no: "",
  name: "",
  company: "",
  email: "",
  business_type: "",
  interested_flows: [],
  interested_ctas: [],
};

// ─── Main Leads Page ──────────────────────────────────────────────────────

const Leads = () => {
  const { getValidToken } = useAuth();
  const tenant = localStorage.getItem("tenant");

  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);

  const [searchText, setSearchText] = useState("");
  const searchTimerRef = useRef(null);

  const [selectedLead, setSelectedLead] = useState(null);

  const fetchLeads = useCallback(
    async (search = searchText, currentPage = page, perPage = rowsPerPage) => {
      const token = await getValidToken();
      setLoading(true);
      try {
        const params = new URLSearchParams({
          offset: currentPage * perPage,
          limit: perPage,
        });
        const trimmed = (search ?? "").trim();
        params.set("search", trimmed);

        const response = await fetch(
          `${LIST_CREATE_BOTBRIDGE_LEADS}?${params}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "X-Tenant-ID": tenant,
            },
          },
        );
        if (!response.ok) throw new Error("Network response was not ok");
        const data = await response.json();
        setLeads(data.results);
        setTotalCount(data.count || 0);
      } catch (err) {
        console.error("Error fetching leads:", err);
        setError(err);
      } finally {
        setLoading(false);
      }
    },
    [getValidToken, tenant, searchText, page, rowsPerPage],
  );

  useEffect(() => {
    fetchLeads(searchText, page, rowsPerPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setPage(0);
      fetchLeads(value, 0, rowsPerPage);
    }, 400);
  };

  const handleChangePage = (_, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Stack sx={{ p: 4 }}>
      {/* Toolbar */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <TextField
          placeholder="Search by name, phone, email…"
          value={searchText}
          onChange={handleSearchChange}
          size="small"
          sx={{ width: 320 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      <AppTable
        columns={COLUMNS}
        rows={leads}
        rowKey="id"
        loading={loading}
        emptyMessage="No leads found"
        maxHeight={600}
        hideMainScroll={false}
        containerSx={{ overflowX: "auto" }}
        count={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[20, 50, 100]}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Stack>
  );
};

export default Leads;
